import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';
import ReportWhatsAppModal from '../../components/ReportWhatsAppModal';

const StockReport = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [stock, setStock] = useState([]);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(isAdmin ? 'ALL' : (user?.firmId || '1'));
  const [loading, setLoading] = useState(true);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const configRes = await axiosClient.get('/auth/config');
        if (configRes.success) setFirms(configRes.data?.firms || []);
      } catch (e) {
        console.error(e);
      }
    };
    if (isAdmin) fetchFirms();
  }, [isAdmin]);

  const fetchStock = async () => {
    setLoading(true);
    try {
      const firmParam = isAdmin ? (selectedFirm || 'ALL') : (user?.firmId || '');
      const res = await axiosClient.get(`/reports/stock?firmId=${firmParam}`);
      if (res.success) {
        setStock(res.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStock();
  }, [selectedFirm]);

  const totalStockQty = stock.reduce((sum, i) => sum + (parseFloat(i.currentStockQty) || 0), 0);
  const totalStockValuation = stock.reduce((sum, i) => sum + ((parseFloat(i.currentStockQty) || 0) * (parseFloat(i.PRate) || 0)), 0);

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 no-print">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-boxes text-primary me-2"></i> Inventory & Stock Report
          </h3>
          <p className="text-muted small mb-0">
            {isAdmin 
              ? 'Multi-Firm Admin Stock: Live calculation across all firms or individual warehouse branches' 
              : `Live Stock Balance for: ${user?.firmName || 'Assigned Branch'}`}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isAdmin && (
            <div className="d-flex align-items-center gap-1 bg-light border p-1 rounded">
              <i className="bi bi-building text-warning ms-1"></i>
              <select
                className="form-select form-select-sm border-0 bg-transparent"
                style={{ width: '220px' }}
                value={selectedFirm}
                onChange={(e) => setSelectedFirm(e.target.value)}
              >
                <option value="ALL">★ All Firms (Consolidated)</option>
                {firms.map((f) => (
                  <option key={f.Id} value={f.Id}>
                    {f.firmName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            className="btn btn-success d-flex align-items-center gap-1"
            onClick={() => setShowWhatsAppModal(true)}
            disabled={stock.length === 0}
          >
            <i className="bi bi-whatsapp"></i> Send WhatsApp
          </button>
          <button className="btn btn-primary d-flex align-items-center gap-1" onClick={() => window.print()}>
            <i className="bi bi-printer me-1"></i> Print Stock Sheet
          </button>
        </div>
      </div>

      <div className="card shadow-sm border-0 print-area">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '60px' }}>#</th>
                  <th>Product Name</th>
                  <th className="text-center">Code</th>
                  <th className="text-center">Unit</th>
                  <th className="text-end">Total Purchased</th>
                  <th className="text-end">Total Sold</th>
                  <th className="text-end">Current Balance Stock</th>
                  <th className="text-end">Valuation @ PRate (₹)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                    </td>
                  </tr>
                ) : stock.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">No stock data available.</td>
                  </tr>
                ) : (
                  stock.map((item, idx) => {
                    const balance = parseFloat(item.currentStockQty || 0);
                    const valuation = balance * (parseFloat(item.PRate) || 0);
                    return (
                      <tr key={item.Id}>
                        <td className="text-muted small">{idx + 1}</td>
                        <td><strong className="text-dark">{item.productName}</strong></td>
                        <td className="text-center"><span className="badge bg-light text-dark border">{item.productCode || '0'}</span></td>
                        <td className="text-center">{item.unit || 'KG'}</td>
                        <td className="text-end text-muted">{parseFloat(item.totalPurchasedQty || 0).toFixed(2)}</td>
                        <td className="text-end text-muted">{parseFloat(item.totalSoldQty || 0).toFixed(2)}</td>
                        <td className={`text-end fw-bold fs-6 ${balance >= 0 ? 'text-primary' : 'text-danger'}`}>
                          {balance.toFixed(2)} {item.unit || 'KG'}
                        </td>
                        <td className="text-end fw-bold text-success">
                          ₹{valuation > 0 ? valuation.toFixed(2) : '0.00'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* WhatsApp Sharing Modal */}
      {showWhatsAppModal && (
        <ReportWhatsAppModal
          show={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          title="Inventory & Stock Report"
          reportType="stock_report"
          reportData={{
            title: 'Inventory & Stock Report',
            subtitle: selectedFirm === 'ALL' ? 'All Firms (Consolidated)' : (firms.find(f => String(f.Id) === String(selectedFirm))?.firmName || user?.firmName || 'Assigned Branch'),
            firmName: (selectedFirm !== 'ALL' && firms.find(f => String(f.Id) === String(selectedFirm))?.firmName) || user?.firmName || 'PUSHPAI SEA FOOD(ALIBAG)',
            stock,
            columns: ['#', 'Product Name', 'Code', 'Unit', 'Purchased', 'Sold', 'Balance Stock', 'Valuation @ PRate'],
            rows: stock.map((item, idx) => {
              const balance = parseFloat(item.currentStockQty || 0);
              const valuation = balance * (parseFloat(item.PRate) || 0);
              return [
                idx + 1,
                item.productName,
                item.productCode || '-',
                item.unit || 'KG',
                parseFloat(item.totalPurchasedQty || 0).toFixed(2),
                parseFloat(item.totalSoldQty || 0).toFixed(2),
                `${balance.toFixed(2)} ${item.unit || 'KG'}`,
                `₹${valuation > 0 ? valuation.toFixed(2) : '0.00'}`
              ];
            }),
            summaryCards: [
              { label: 'Total Varieties', value: `${stock.length} Products` },
              { label: 'Total Live Stock', value: `${totalStockQty.toFixed(2)} KG` },
              { label: 'Total Valuation', value: `₹${totalStockValuation.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` }
            ]
          }}
        />
      )}
    </div>
  );
};

export default StockReport;
